import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMediaScripts } from '.';
import { getApiUrl, getSearchResultsByEmail, getSearchResultsWithEmail } from '../../api';
import { HasClassName } from '../../component-utils';
import { useSelector } from '../../hook-utils';
import { showFileUploadDialog } from '../../legacy';
import { Attachment, CallState, CallType, ClidObjectType, ContactType, EmailReplyType, EntityType, Identifier, MatchFieldType, Media, QueuingState, SearchEntity } from '../../model';
import { actions } from '../../store';
import { selectConfigProp, selectMyself } from '../../store/reducers/config';
import { selectEmail, selectEmailSending, selectReplyEmail, Sending } from '../../store/reducers/emails';
import { selectMedia } from '../../store/reducers/media';
import { selectExtension } from '../../store/reducers/queueAgents';
import Autocomplete, { useDebouncedCallback } from '../Autocomplete';
import Button from '../Button';
import HtmlEditor, { HtmlEditorApi } from '../HtmlEditor';
import { ReactComponent as AttachmentIcon } from '../icons/attachment.svg';
import { ReactComponent as DeleteIcon } from '../icons/callcontrol/Delete.svg';
import { ReactComponent as ForwardIcon } from '../icons/callcontrol/email-forward.svg';
import { ReactComponent as ReplyIcon } from '../icons/callcontrol/email-reply.svg';
import { ReactComponent as ReplyAllIcon } from '../icons/callcontrol/email-reply_all.svg';
import { ReactComponent as AcceptIcon } from '../icons/callcontrol/Retrieve.svg';
import { ReactComponent as ContactIcon } from '../icons/facebook-user.svg';
import { ReactComponent as MoreIcon } from '../icons/more.svg';
import { ReactComponent as AgentIcon } from '../icons/toolbar-agent.svg';
import Input from '../Input';
import MediaControl from '../MediaControl';
import { Menu, MenuItem, MenuTarget, targetFromEvent } from '../Menu';
import './EmailView.scss';
import { ParkCallControlButtons } from './FacebookView';

type AttachmentMenuState = { target: MenuTarget, mediaId: string, emailId: string, originalId?: string, item?: Attachment, state: 'read' | 'write' }
type MoreMenuState = { target: MenuTarget, mediaId: string, emailId: string, state: 'read' | 'write' }


interface Props extends HasClassName {
  media: Media
}

interface PropsViewer extends HasClassName {
  mediaId: string
}

type PropsEditor = PropsViewer

export default function EmailView({ className, media }: Props) {
  const email = useSelector(s => selectEmail(s, media.id))
  const dispatch = useDispatch()

  useEffect(() => {
    if (email || (media.mediaType === 'EMAIL' && media.callType !== CallType.Outgoing)) { return }

    dispatch(actions.requestCreateNewOutGoing(media.id))
  }, [dispatch, email, media.callType, media.id, media.mediaType])

  if (media.callType === CallType.Outgoing) {

    if (!email) { return <div className='EmailView'>loading...</div> }

    return <EmailEditor mediaId={media.id} className={className} />

  } else {
    if (email && email.reply) {
      return <EmailEditor mediaId={media.id} className={className} />
    } else {
      return <EmailViewer media={media} className={className} />
    }
  }
}

export function EmailViewer({ className, media }: Props) {
  const email = useSelector(s => selectEmail(s, media.id))
  const dispatch = useDispatch()

  useEffect(() => {
    if (email) { return }

    dispatch(actions.fetchEmailReadRequest(media.mediaItemId, media.id))

  }, [dispatch, email, media.id, media.mediaItemId])

  if (!email) { return <div className='EmailView'>loading...</div> }

  return <EmailViewerRender mediaId={media.id} />
}

export function EmailViewerRender({ className, mediaId }: PropsViewer) {
  const myId = useSelector(selectMyself)
  const extension = useSelector(s => myId && selectExtension(s, myId))
  const email = useSelector(s => selectEmail(s, mediaId))
  const media = useSelector(s => selectMedia(s, mediaId))
  const emailAttachmentPreview = useSelector(s => selectConfigProp(s, 'emailAttachmentPreview'))
  const dispatch = useDispatch()
  const autoReply = email && email.original.subject.indexOf("Automatic Reply") === 0 ? true : false
  const refAtttachments = useRef<HTMLDivElement>(null)
  const refAttachment = useRef<HTMLAnchorElement[]>([]);
  const [hidden, setHidden] = useState<HTMLAnchorElement[]>([])
  const [moreMenu, setMoreMenu] = useState<MoreMenuState>()

  useEffect(() => {
    if (refAtttachments.current) {
      const h: HTMLAnchorElement[] = []
      const width = refAtttachments.current.clientWidth + refAtttachments.current.getBoundingClientRect().left
      for (let index = 0; index < refAttachment.current.length; index++) {
        const element = refAttachment.current[index];
        const left = element.getBoundingClientRect().left
        if (left > width) {
          h.push(element)
        }
      }
      setHidden(h)
    }

  }, [refAttachment])

  const items = useMemo(() => {
    if (!email || !emailAttachmentPreview) return []

    return hidden.map((item) => {
      const a = email.original.attachments.find(x => x.id.toLowerCase() === item.id.toLowerCase())
      if (a) {
        return <MenuItem onClick={() => dispatch(actions.downloadFile(getApiUrl('/Uploads/Queue/' + a.serverFilePath), previewAttachments(emailAttachmentPreview, a.serverFilePath), a.name))}>
          <AttachmentIcon className={'padded'} />
          {a.name} ({Math.round(a.size / 1000)} kb)
      </MenuItem>
      } else {
        return <></>
      }
    })

  }, [dispatch, email, emailAttachmentPreview, hidden])


  if (!myId || !extension || !emailAttachmentPreview) { return null }

  if (!email) { return <div className='EmailView'>loading...</div> }

  function reply(type: EmailReplyType) {
    return dispatch(actions.replyToEmail(mediaId, type, false))
  }

  let preview = false;

  //ReceivedBy will be pouplated with the Email account that received the incoming email.
  //For a sent email, it will be empty.
  if (email.original.receivedBy.address !== email.original.sender.address) {
    if (media) { // datastore delivered
      preview = false;
    } else {
      switch (email.original.queuingState) {
        case QueuingState.PendingQueueAssignment:
        case QueuingState.NotQueued:
        case QueuingState.Queued:
          preview = true;
          break;
        default:
          preview = false;
      }
    }
  } else {
    preview = false;
  }

  const onAttachmentsRefs = (el: HTMLAnchorElement | null) => {
    if (el && refAttachment.current.findIndex(x => x.id === el.id) === -1) {
      refAttachment.current.push(el);
    }
  }

  return <div className={classNames('EmailView', className)}>
    <MediaControl className='toolbar'>
      {preview && <button onClick={() => dispatch(actions.requestMediaTakeItem(mediaId, 'EMAIL'))} title='Accept'><AcceptIcon /></button>}
      {!preview && (media == null || (media && media.status !== CallState.QueueHold && media.deviceId === myId.id)) && !autoReply && email.original.subject.indexOf && <button onClick={() => reply(EmailReplyType.Reply)} title='Reply'>
        <ReplyIcon />
      </button>}
      {!preview && (media == null || (media && media.status !== CallState.QueueHold && media.deviceId === myId.id)) && !autoReply && <button onClick={() => reply(EmailReplyType.ReplyAll)} title='Reply all'>
        <ReplyAllIcon />
      </button>}
      {!preview && (media == null || (media && media.status !== CallState.QueueHold && media.deviceId === myId.id)) && !autoReply && <button onClick={() => reply(EmailReplyType.Forward)} title='Forward'>
        <ForwardIcon />
      </button>}
      {!preview && media && <ParkCallControlButtons media={media} />}
    </MediaControl>

    <div className='fields narrow'>
      <div className='field'>
        <div className='fieldName'>From</div>
        <div className='fieldValue'>
          {email.original.from.name} &lt;{email.original.from.address}&gt;
        </div>
      </div>

      <div className='field'>
        <div className='fieldName'>To</div>
        <div className='fieldValue'>
          {email.original.toRecipients.map(r => r.address).join(', ')}
        </div>
      </div>

      {email.original.ccRecipients.length > 0 && <div className='field'>
        <div className='fieldName'>Cc</div>
        <div className='fieldValue'>
          {email.original.ccRecipients.map(r => r.address).join(', ')}
        </div>
      </div>}

      <div className='field'>
        <div className='fieldName'>Subject</div>
        <div className='fieldValue'>{email.original.subject}</div>
      </div>

      <div className='field'>
        <div className='fieldName'>Date</div>
        <div className='fieldValue'>{email.original.sentAtUtc ? dayjs(email.original.sentAtUtc).format('LL LT') : dayjs(email.original.receivedAtUtc).format('LL LT')}</div>
      </div>
      {email.original.attachments.length > 0 && <div className='field'>
        <div className='fieldName'>Attachments</div>
        <div ref={refAtttachments} className='fieldValue'>
          <div className='attachments'>
            {email.original.attachments.map(a => {
              if (!previewAttachments(emailAttachmentPreview, a.serverFilePath)) {
                return <a ref={onAttachmentsRefs} key={a.id} id={a.id} className="attachment" download href={getApiUrl('/Uploads/Queue/' + a.serverFilePath)} title={a.name + ' (' + (Math.round(a.size / 1000) + ' kb)')}>
                  <div><AttachmentIcon /></div>
                  <div>{a.name} ({Math.round(a.size / 1000)} kb)</div>
                </a>
              } else {
                // eslint-disable-next-line react/jsx-no-target-blank
                return <a ref={onAttachmentsRefs} key={a.id} id={a.id} className='attachment' target='_blank' href={getApiUrl('/Uploads/Queue/' + a.serverFilePath)} title={a.name + ' (' + (Math.round(a.size / 1000) + ' kb)')}>
                  <div><AttachmentIcon /></div>
                  <div>{a.name} ({Math.round(a.size / 1000)} kb)</div>
                </a>
              }
            })}
          </div>
          {hidden.length > 0 && <div className='more' onClick={(e) => setMoreMenu({ emailId: email.original.id, mediaId: mediaId, target: targetFromEvent(e), state: 'write' })}>
            <MoreIcon />
          </div>}
        </div>
      </div>}
    </div>
    {moreMenu && <Menu open={true} onClose={() => setMoreMenu(undefined)} target={moreMenu.target} placement='bottom-end'>{items}</Menu>}
    <RenderViewer body={email.original.body} />
  </div >
}

function previewAttachments(emailAttachmentPreview: string[], file: string) {
  let preview = false

  emailAttachmentPreview.forEach(element => {
    if (file.indexOf(element) !== -1) {
      preview = true
      return
    }
  });

  return preview
}

function RenderViewer({ body }: { body: string }) {
  return <iframe className='viewer' srcDoc={body} title='email'
    onAbort={(e) => {
      e.stopPropagation()
      e.preventDefault()
      console.info('RenderViewer onAbort')
    }}
    onLoad={(e) => {
      e.stopPropagation()
      e.preventDefault()
    }}
    onError={(e) => {
      e.stopPropagation()
      e.preventDefault()
      console.info('RenderViewer onError')
    }} />
}

export function EmailEditor({ className, mediaId }: PropsEditor) {
  const myself = useSelector(selectMyself)
  const media = useSelector(s => selectMedia(s, mediaId))
  const email = useSelector(s => selectReplyEmail(s, mediaId))
  const sending = useSelector(s => selectEmailSending(s, mediaId))
  const [menu, setMenu] = useState<AttachmentMenuState>()
  const dispatch = useDispatch()
  const editorRef = React.useRef<HtmlEditorApi | null>(null)
  const [toContact, setToContact] = useState<SearchEntity>()
  const [ccContact, setCcContact] = useState<SearchEntity>()

  const [count, setCount] = useState(0)
  const refAtttachments = useRef<HTMLDivElement>(null)
  const refAttachment = useRef<HTMLDivElement[]>([]);
  const [hidden, setHidden] = useState<HTMLDivElement[]>([])
  const [moreMenu, setMoreMenu] = useState<AttachmentMenuState>()

  useEffect(() => {
    if (refAtttachments.current) {
      const h: HTMLDivElement[] = []
      for (let index = 0; index < count; index++) {
        const element = refAttachment.current[index];
        const left = element.getBoundingClientRect().left
        const width = refAtttachments.current.clientWidth
        if (left > width) {
          h.push(element)
        }
      }
      setHidden(h)
    }
  }, [count])

  const items = useMemo(() => {
    if (!email) return []
    let x: Attachment[] = []
    hidden.forEach(element => {
      const y = email.attachments.find(x => x.id.toLowerCase() === element.id.toLowerCase())
      if (y) {
        x.push(y)
      }
    });
    return x
  }, [email, hidden])

  useMediaScripts(html => {
    const editor = editorRef.current
    if (!editor) { return }

    editor.pasteContent(html)
  })

  // HACK: win the focus war with the Notes field
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const editor = editorRef.current
      if (editor) { editor.focus() }
    }, 100)
    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (email && toContact && toContact.email !== '') {
        const oldValue = email.to.lastIndexOf(';') === -1 ? '' : email.to.substring(0, email.to.lastIndexOf(';') + 1)
        onToChange(oldValue + ((oldValue === '') ? '' : ' ') + toContact.email + ';')
      }
    }, 100)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toContact])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (email && ccContact && ccContact.email !== '') {
        const oldValue = email.cc.lastIndexOf(';') === -1 ? '' : email.cc.substring(0, email.cc.lastIndexOf(';') + 1)
        onCcChange(oldValue + ((oldValue === '') ? '' : ' ') + ccContact.email + ';')
      }
    }, 100)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccContact])

  const startMatchTo = useDebouncedCallback(async (to: string, myself: Identifier | null, media?: Media) => {
    if (!myself || !media) return

    const reply: string[] = []
    to.split(';').forEach(e => {
      reply.push(e)
    })

    for (let index = 0; index < reply.length; index++) {

      //find contact on the server if you can
      const data = await getSearchResultsByEmail(myself, reply[index])

      //set media if not already set
      if (data.length === 1 && data[0].matchedFieldType === MatchFieldType.MatchEmail && data[0].matchedValue === reply[index]) {
        let type = ClidObjectType.none
        let typeString = ''
        if (data[0].entityType === EntityType.Contact) {
          if (data[0].entitySubType === ContactType.Personal) {
            type = ClidObjectType.personalContact
            typeString = 'contact'
          } else if (data[0].entitySubType === ContactType.Company) {
            type = ClidObjectType.companyContact
            typeString = 'contact'
          } else if (data[0].entitySubType === ContactType.Customer) {
            type = ClidObjectType.customerContact
            typeString = 'contact'
          }
        } else if (data[0].entityType === EntityType.Extension) {
          type = ClidObjectType.extension
          typeString = 'extension'
        }

        if (type !== ClidObjectType.none) {
          //update media with matched entity
          dispatch(actions.updateMediaClidObject(mediaId, data[0].entityId, type, data[0].description))
          //notify users
          dispatch(actions.showClientNotification('Email address has matched ' + typeString + ' \'' + data[0].description + '\''))
          //stop matchin
          index = reply.length
        }
      }
    }

  }, 300)

  if (!myself) { return null }

  if (!email) { return <>loading...</> }

  function onToChange(value: string) {

    dispatch(actions.setEmailTo(mediaId, value))

    //lets get a match if is out going
    if (media && media.callType === CallType.Outgoing) {
      startMatchTo(value, myself, media)

      dispatch(actions.updateMediaClid(mediaId, value))
    }
  }

  function onCcChange(value: string) {
    dispatch(actions.setEmailCc(mediaId, value))
  }

  const onAttachmentsRefs = (el: HTMLDivElement | null) => {
    if (el && refAttachment.current.findIndex(x => x.id === el.id) === -1) {
      refAttachment.current.push(el);

      setCount(refAttachment.current.length)
    }
  }

  return <div className={classNames('EmailView', className)}>
    <div className='fields'>
      <div className='field'>
        <div className='fieldName'>To</div>
        <div className='fieldValue'>
          <ToLookup
            disabled={(sending === Sending.Sending || sending === Sending.Sent)}
            value={email.to}
            onChange={value => onToChange(value)}
            placeholder='Search Contact Emails'
            onSelected={item => {
              setToContact(item)
              return item.email
            }} />
          <Button disabled={(sending === Sending.Sending || sending === Sending.Sent)} onClick={() => dispatch(actions.cancelReply(mediaId))}>Cancel</Button>
          <Button disabled={(sending === Sending.Sending || sending === Sending.Sent) || !email.to || !email.subject} primary onClick={() => dispatch(actions.sendEmail(mediaId))}>{sending === Sending.Sending ? 'Sending...' : sending === Sending.Sent ? 'Sent' : 'Send'}</Button>
        </div>
      </div>

      <div className='field'>
        <div className='fieldName'>CC</div>
        <div className='fieldValue'>
          <ToLookup
            disabled={(sending === Sending.Sending || sending === Sending.Sent)}
            value={email.cc}
            onChange={value => onCcChange(value)}
            placeholder='Search Contact Emails'
            onSelected={item => {
              setCcContact(item)
              return item.email
            }} />
        </div>
      </div>

      <div className='field'>
        <div className='fieldName'>Subject</div>
        <div className='fieldValue'>
          <Input disabled={(sending === Sending.Sending || sending === Sending.Sent)} onFocus={(e) => {
            if (e.currentTarget.value === 'New queue email') {
              dispatch(actions.setEmailSubject(mediaId, ''))
            }
          }} value={email.subject} onChange={e => dispatch(actions.setEmailSubject(mediaId, e.currentTarget.value))} />
        </div>
      </div>
      <div className='field'>
        <div className='fieldName'>Attachments</div>
        <div ref={refAtttachments} className='fieldValue'>
          <div className='attachments'>
            {email.attachments.filter(a => !a.isInLine).map(a => {
              const title = a.name + ' (' + Math.round(a.size / 1000) + ' kb)'
              return <div ref={(e) => onAttachmentsRefs(e)} id={a.id} key={a.id} className='attachment' title={title} onClick={(e) => setMenu({ emailId: email.id, mediaId: mediaId, target: targetFromEvent(e), item: a, state: 'write' })}>
                <div><AttachmentIcon /></div>
                <div>{title}</div>
              </div>
            })}
          </div>
          <div className='more' onClick={(e) => setMoreMenu({ emailId: email.id, mediaId: mediaId, target: targetFromEvent(e), state: 'write', originalId: email.originalId })}>
            <MoreIcon />
          </div>
        </div>
      </div>
    </div>

    <HtmlEditor className='editor' editorRef={editorRef} value={email.body} onChange={v => dispatch(actions.setEmailBody(mediaId, v))} disabled={(sending === Sending.Sending || sending === Sending.Sent)} autoFocus />
    {menu && <AttachmentFileMenu state={menu} onClose={() => setMenu(undefined)} />}
    {moreMenu && <UploadFileMenu state={moreMenu} onClose={() => setMoreMenu(undefined)} attachments={items} />}
  </div>
}

function AttachmentFileMenu({ state: { mediaId, emailId, item, target, state }, onClose }: { state: AttachmentMenuState, onClose(): void }) {
  const dispatch = useDispatch()
  if (!item) return null

  return <Menu open={true} onClose={onClose} target={target} placement='bottom-end'>
    {state === 'write' && <MenuItem onClick={() => dispatch(actions.removeEmailFileAttachment(mediaId, emailId, item.id))}>
      <DeleteIcon className='padded' />
      Remove
    </MenuItem>}
    {state === 'read' && <MenuItem onClick={() => dispatch(actions.downloadFile(getApiUrl('/Uploads/Queue/' + item.serverFilePath), true, item.name))}>

      Preview
    </MenuItem>}
    {state === 'read' && <MenuItem onClick={() => dispatch(actions.downloadFile(getApiUrl('/Uploads/Queue/' + item.serverFilePath), false, item.name))}>

      Download
    </MenuItem>}
  </Menu>
}

function UploadFileMenu({ state: { mediaId, emailId, originalId, item, target }, onClose, attachments }: { state: AttachmentMenuState, onClose(): void, attachments: Attachment[] }) {
  const dispatch = useDispatch()

  return <Menu open={true} onClose={onClose} target={target} placement='bottom-end'>
    <MenuItem onClick={() => {
      showFileUploadDialog(emailId, originalId ? originalId : '', (id, result) => {
        if (result != null) {
          dispatch(actions.setEmailFileAttachment(mediaId, emailId, {
            id: id,
            isInLine: false,
            isItem: false,
            contentId: '',
            contentType: result.ContentType,
            name: result.Name,
            serverFilePath: result.ServerFilePath,
            clientFilePath: '',
            size: result.Size,
          }))
        }
      })
    }}>
      <AttachmentIcon className='padded' />
      Add Attachment
    </MenuItem>
    {attachments.map(a => {
      return <MenuItem onClick={() => dispatch(actions.removeEmailFileAttachment(mediaId, emailId, a.id))} key={'di-' + a.id} >
        <DeleteIcon className='padded' />
        {a.name} ({Math.round(a.size / 1000)} kb)
  </MenuItem>
    })}
  </Menu>
}

type ToLookupProps = HasClassName & {
  enabled?: boolean
  disabled?: boolean
  autoFocus?: boolean
  placeholder?: string
  value: string
  onChange: (value: string) => void
  onSelected: (item: SearchEntity) => string
  onKeyUp?(event: React.KeyboardEvent<HTMLInputElement>): void
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void
}

function ToLookup({ className, disabled, onSelected, placeholder, value, onChange, onKeyUp, onKeyDown, autoFocus, enabled }: ToLookupProps) {
  const mySelf = useSelector(selectMyself)
  const set = useRef<string>()
  const selected = useRef<string>()

  async function searchForEmailsAddress(input: string) {
    if (!set.current) {
      set.current = input
      return []
    }

    if (!input || !mySelf || selected.current === input || enabled === false) { return [] }
    const filter = input.substr(input.lastIndexOf(';') + 1)
    if (!filter) { return [] }

    return await getSearchResultsWithEmail(mySelf, filter, true)
  }

  return <Autocomplete<SearchEntity> disabled={disabled} value={value} onChange={e => onChange(e.currentTarget.value)} placeholder={placeholder} getItems={searchForEmailsAddress}
    renderItem={item => {
      const icon = item.entityType === EntityType.Contact ? <ContactIcon /> : <AgentIcon />
      return <>{icon} {item.description}</>
    }} autocomplete={autoFocus} onSelected={(item) => {
      if (onSelected) {
        const x = onSelected(item)
        selected.current = x
      }
    }} />

}
